import { lazy, Suspense } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import DeferredToastContainer from './components/DeferredToastContainer';
import { PasswordResetRedirector } from './components/PasswordResetRedirector';
import AdminRoute from './components/AdminRoute';
import AppChrome from './components/AppChrome';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import FavoriteProvider from './context/FavoriteContext';
import ViewHistoryProvider from './context/ViewHistoryContext';
import Loading from './components/ui/Loading';
// Catálogo eager (LCP da home); auth/favoritos/admin em lazy
import ProductCatalog from './pages/ProductCatalog';

const FavoritesPage = lazy(() =>
  import(/* webpackChunkName: "favorites-page" */ './pages/FavoritesPage')
);
const Login = lazy(() =>
  import(/* webpackChunkName: "auth-login" */ './pages/Login')
);
const Register = lazy(() =>
  import(/* webpackChunkName: "auth-register" */ './pages/Register')
);
const AdminLayout = lazy(() =>
  import(/* webpackChunkName: "admin-layout" */ './layouts/AdminLayout')
);

// Lazy load componentes não críticos para melhorar FCP
const ProductDetails = lazy(() => import('./pages/ProductDetails'));
const Analytics = lazy(() => import('./components/Analytics'));

// ✅ Lazy loading de páginas (melhora performance inicial)
const AdminOrderDetails = lazy(() => import('./pages/AdminOrderDetails'));
const AdminOrdersPage = lazy(() => import('./pages/AdminOrdersPage'));
const CartPage = lazy(() => import('./pages/CartPage'));
const CategoryProducts = lazy(() => import('./pages/CategoryProducts'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const ConfirmationPage = lazy(() => import('./pages/ConfirmationPage'));
const ContactForm = lazy(() => import('./pages/ContactForm'));
const CreatePedidoPage = lazy(() => import('./pages/CreatePedidoPage'));
const CreateProductPage = lazy(() => import('./pages/CreateProductPage'));
const AdminProductsPage = lazy(() => import('./pages/admin/AdminProductsPage'));
const ProductEditorPage = lazy(() => import('./pages/admin/ProductEditorPage'));
const AdminCategoriesPage = lazy(() => import('./pages/admin/AdminCategoriesPage'));
const AdminStockPage = lazy(() => import('./pages/admin/AdminStockPage'));
const AdminCustomersPage = lazy(() => import('./pages/admin/AdminCustomersPage'));
const AdminPaymentIssuesPage = lazy(() => import('./pages/admin/AdminPaymentIssuesPage'));
const FailurePage = lazy(() => import('./pages/FailurePage'));
const GarantiaDevolucoes = lazy(() => import('./pages/GarantiaDevolucoes'));
const OrderDetailsPage = lazy(() => import('./pages/OrderDetailsPage'));
const OrderHistoryPage = lazy(() => import('./pages/OrderHistoryPage'));
const OrderSummaryPage = lazy(() => import('./pages/OrderSummaryPage'));
const OrderConfirmationPage = lazy(() => import('./pages/OrderConfirmationPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const PaymentStatusChecker = lazy(() => import('./pages/PaymentStatusChecker'));
const PaymentStatusPage = lazy(() => import('./pages/PaymentStatusPage'));
const PendingPage = lazy(() => import('./pages/PendingPage'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PaymentStatus = lazy(() => import('./pages/payment/PaymentStatus'));
const PaymentConfirmation = lazy(() => import('./pages/payment/PaymentConfirmation'));
const PaymentChecker = lazy(() => import('./pages/payment/PaymentChecker'));
const PoliticaPrivacidade = lazy(() => import('./pages/PoliticaPrivacidade'));
const TermosCondicoes = lazy(() => import('./pages/TermosCondicoes'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const ShippingAddressPage = lazy(() => import('./pages/ShippingAddressPage'));
const SobreNos = lazy(() => import('./pages/SobreNos'));
const SuccessPage = lazy(() => import('./pages/SuccessPage'));
const PayPalReturnPage = lazy(() => import('./pages/PayPalReturnPage'));
const PayPalStatusPage = lazy(() => import('./pages/PayPalStatusPage'));
const NotificationAdmin = lazy(() => import('./pages/NotificationAdmin'));
const PushTestPage = lazy(() => import('./pages/PushTestPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminCampaignsPage = lazy(() => import('./pages/admin/AdminCampaignsPage'));
const AdminAbandonedCartsPage = lazy(() => import('./pages/admin/AdminAbandonedCartsPage'));
const AdminReviewsPage = lazy(() => import('./pages/admin/AdminReviewsPage'));
const AdminLogsPage = lazy(() => import('./pages/admin/AdminLogsPage'));
const AdminToolsPage = lazy(() => import('./pages/admin/AdminToolsPage'));
const AdminRmaPage = lazy(() => import('./pages/admin/AdminRmaPage'));
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const LoyaltyDashboard = lazy(() => import('./pages/LoyaltyDashboard'));
const RmaPage = lazy(() => import('./pages/RmaPage'));
const ClubeDoTecnicoPage = lazy(() => import('./pages/ClubeDoTecnicoPage'));
const AccountPrivacyPage = lazy(() => import('./pages/AccountPrivacyPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const CartRecoverPage = lazy(() => import('./pages/CartRecoverPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Componente de fallback para Suspense
const SuspenseFallback = () => (
  <Loading fullScreen message="Carregando página..." />
);

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <FavoriteProvider>
          <ViewHistoryProvider>
            <Router>
              <PasswordResetRedirector />
              <Suspense fallback={null}>
                <Analytics />
              </Suspense>
              <AppChrome>
                <DeferredToastContainer position="top-right" autoClose={3000} />
                <main role="main" id="main-content">
                  <Suspense fallback={<SuspenseFallback />}>
                    <Routes>
                      <Route path="/clube-do-tecnico" element={<ClubeDoTecnicoPage />} />
                      <Route path="/sobre" element={<SobreNos />} />
                      <Route path="/garantia" element={<GarantiaDevolucoes />} />
                      <Route path="/privacidade" element={<PoliticaPrivacidade />} />
                      <Route path="/unsubscribe" element={<UnsubscribePage />} />
                      <Route path="/cart/recover/:token" element={<CartRecoverPage />} />
                      <Route path="/termos-e-condicoes" element={<TermosCondicoes />} />
                      <Route path="/contato" element={<ContactForm />} />

                      {/* Push de teste: só admin (e redirect da rota pública antiga) */}
                      <Route path="/push" element={<Navigate to="/admin/push" replace />} />

                      {/* ============================================
                          ✅ NOVAS ROTAS CONSOLIDADAS DE PAGAMENTO
                          ============================================ */}

                      <Route
                        path="/pagamento/status/:status"
                        element={
                          <ProtectedRoute>
                            <PaymentStatus />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/pagamento/status/:status/:externalReference"
                        element={
                          <ProtectedRoute>
                            <PaymentStatus />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/pagamento/confirmacao/:externalReference"
                        element={
                          <ProtectedRoute>
                            <PaymentConfirmation />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/pagamento/consultar"
                        element={
                          <ProtectedRoute>
                            <PaymentChecker />
                          </ProtectedRoute>
                        }
                      />

                      {/* ============================================
                          🔵 ROTAS ANTIGAS (MANTIDAS - COMPATIBILIDADE TOTAL)
                          ============================================ */}

                      <Route
                        path="/pending"
                        element={
                          <ProtectedRoute>
                            <PendingPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/pending/:externalReference"
                        element={
                          <ProtectedRoute>
                            <PendingPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/failure" element={<FailurePage />} />
                      <Route path="/success" element={<SuccessPage />} />
                      <Route
                        path="/paypal/return"
                        element={
                          <ProtectedRoute>
                            <PayPalReturnPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/paypal/success"
                        element={
                          <ProtectedRoute>
                            <PayPalStatusPage status="success" />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/paypal/failure" element={<PayPalStatusPage status="failure" />} />
                      <Route
                        path="/paypal/pending"
                        element={
                          <ProtectedRoute>
                            <PayPalStatusPage status="pending" />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/paypal/pending/:externalReference"
                        element={
                          <ProtectedRoute>
                            <PayPalStatusPage status="pending" />
                          </ProtectedRoute>
                        }
                      />

                      <Route path="/register" element={<Register />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/" element={<ProductCatalog />} />

                      <Route path="/search" element={<SearchResults />} />

                      <Route path="/products/:id" element={<ProductDetails />} />

                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/favoritos" element={<FavoritesPage />} />
                      <Route
                        path="/wishlist"
                        element={<Navigate to="/favoritos" replace />}
                      />
                      <Route path="/wishlist/:token" element={<WishlistPage />} />
                      <Route
                        path="/favoritos/compartilhada/:token"
                        element={<WishlistPage />}
                      />
                      <Route path="/order-summary" element={<OrderSummaryPage />} />
                      <Route path="/create-pedido" element={<CreatePedidoPage />} />
                      <Route
                        path="/checkout"
                        element={
                          <ProtectedRoute>
                            <CheckoutPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/minha-conta/pedidos"
                        element={
                          <ProtectedRoute>
                            <OrderHistoryPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/minha-conta/fidelidade"
                        element={
                          <ProtectedRoute>
                            <LoyaltyDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/minha-conta/garantia"
                        element={
                          <ProtectedRoute>
                            <RmaPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/minha-conta/privacidade"
                        element={
                          <ProtectedRoute>
                            <AccountPrivacyPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/pedido/:id"
                        element={
                          <ProtectedRoute>
                            <OrderDetailsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/order-details/:id"
                        element={
                          <ProtectedRoute>
                            <OrderDetailsPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/confirmacao/:externalReference"
                        element={
                          <ProtectedRoute>
                            <ConfirmationPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/order-confirmation"
                        element={
                          <ProtectedRoute>
                            <OrderConfirmationPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/pagamento"
                        element={
                          <ProtectedRoute>
                            <PaymentPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/payment-status"
                        element={
                          <ProtectedRoute>
                            <PaymentStatusChecker />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/minha-conta/payment-status"
                        element={
                          <ProtectedRoute>
                            <PaymentStatusPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/envio"
                        element={
                          <ProtectedRoute>
                            <ShippingAddressPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Redirect legado → rota normalizada */}
                      <Route
                        path="/notificationAdmin"
                        element={<Navigate to="/admin/notifications" replace />}
                      />

                      {/* 🔐 PAINEL ADMIN — shell dedicado (sem Header/Footer da loja) */}
                      <Route
                        path="/admin"
                        element={
                          <AdminRoute>
                            <AdminLayout />
                          </AdminRoute>
                        }
                      >
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<AdminDashboard />} />
                        <Route path="orders" element={<AdminOrdersPage />} />
                        <Route path="orders/:id" element={<AdminOrderDetails />} />
                        <Route path="payment-issues" element={<AdminPaymentIssuesPage />} />
                        <Route path="create-product" element={<CreateProductPage />} />
                        <Route path="products" element={<AdminProductsPage />} />
                        <Route path="products/new" element={<ProductEditorPage />} />
                        <Route path="products/:id" element={<ProductEditorPage />} />
                        <Route path="categories" element={<AdminCategoriesPage />} />
                        <Route path="stock" element={<AdminStockPage />} />
                        <Route path="customers" element={<AdminCustomersPage />} />
                        <Route path="notifications" element={<NotificationAdmin />} />
                        <Route path="campaigns" element={<AdminCampaignsPage />} />
                        <Route path="abandoned-carts" element={<AdminAbandonedCartsPage />} />
                        <Route path="reviews" element={<AdminReviewsPage />} />
                        <Route path="rma" element={<AdminRmaPage />} />
                        <Route path="logs" element={<AdminLogsPage />} />
                        <Route path="tools" element={<AdminToolsPage />} />
                        {process.env.NODE_ENV !== 'production' ? (
                          <Route path="push" element={<PushTestPage />} />
                        ) : (
                          <Route path="push" element={<Navigate to="/admin/dashboard" replace />} />
                        )}
                      </Route>

                      <Route path="/categoria/:slug" element={<CategoryProducts />} />

                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                </main>
              </AppChrome>
            </Router>
          </ViewHistoryProvider>
        </FavoriteProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
